// src/features/dashboard/components/TaskQueueDataGridWrapper.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Hooks
import { useTasks } from '../../tasks/hooks/useTasks';
import { useTaskActions } from '../../tasks/hooks/useTaskActions';
import { usePermissions } from '../../auth/hooks/usePermissions';
import useCachedData from '../hooks/useCachedData';
import { CACHE_KEYS } from '../utils/dashboardCache';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

// Components
import TasksDataGrid from '../../tasks/components/TaskListPage/TasksDataGrid';
import GlobalTaskActionsDialog from '../../tasks/components/GlobalTaskActionsDialog';

const ACTIVE_STATES = ['SCHEDULED', 'PENDING', 'IN_PROGRESS', 'ON_HOLD'];

// Soonest due date first; overdue tasks have the earliest due dates, so they
// naturally end up at the top.
const DUE_DATE_SORT_MODEL = [{ field: 'due_date', sort: 'asc' }];

const TaskQueueDataGridWrapper = React.memo(function TaskQueueDataGridWrapper({
  maxRows = 25,
  autoRefresh = true,
  refreshInterval = 300000, // 5 minutes
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // State
  const [showMyTasks, setShowMyTasks] = useState(true);

  // API hooks
  const {
    fetchAllMyTasks,
    filterTasks,
    deleteTask,
  } = useTasks();

  // Create cache key based on view mode
  const cacheKey = showMyTasks ? CACHE_KEYS.MY_TASKS : CACHE_KEYS.DEPARTMENT_TASKS;

  // Fetch function based on view mode
  const fetchFunction = useCallback(async () => {
    // Department view: only the open, current occurrence of each task. The
    // plain department list is newest-scheduled first and includes completed
    // history, so a limit on it could cut off the tasks due soonest.
    const result = showMyTasks
      ? await fetchAllMyTasks()
      : await filterTasks({ states: ACTIVE_STATES, has_recurred: false, limit: 1000 });

    if (result.success) {
      let allTasks = [];
      
      if (showMyTasks) {
        // For "My Tasks", combine primary and secondary tasks
        const primaryTasks = (result.data.primary?.tasks || []).map(task => ({
          ...task,
          assignment_type: 'primary'
        }));
        
        const secondaryTasks = (result.data.secondary?.tasks || []).map(task => ({
          ...task,
          assignment_type: 'secondary'
        }));
        
        allTasks = [...primaryTasks, ...secondaryTasks];
      } else {
        allTasks = result.data?.data?.tasks || [];
      }

      // Filter to show only active tasks
      const activeTasks = allTasks
        .filter(task => !['COMPLETED', 'CANCELLED'].includes(task.state))
        // Sort before slicing so the rows kept are the ones due soonest
        .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
        .slice(0, maxRows);
      
      return {
        success: true,
        data: activeTasks
      };
    }
    
    return result;
  }, [showMyTasks, maxRows, fetchAllMyTasks, filterTasks]);

  // Cached data
  const {
    data: tasksData,
    loading,
    isBackgroundUpdate,
    error,
    refresh: refreshTasks
  } = useCachedData(
    cacheKey,
    fetchFunction,
    { 
      autoRefresh, 
      refreshInterval,
      // Use shorter cache for task queue since it changes more frequently
      cacheMaxAge: 180000 // 3 minutes
    }
  );

  // Task actions hook
  const {
    handleStateChange,
    dialogStates,
    dialogHandlers,
  } = useTaskActions({
    refreshTasks: refreshTasks, // Refresh cached dashboard data
    onSuccess: (data) => {
      console.log('Dashboard task action completed:', data);
    }
  });

  // Ensure tasks is always an array
  const tasks = Array.isArray(tasksData) ? tasksData : [];

  // Event handlers
  const handleViewModeToggle = useCallback((event) => {
    setShowMyTasks(event.target.checked);
  }, []);

  const handleViewTask = useCallback((task) => {
    navigate(`/tasks/${task.task_id}`);
  }, [navigate]);

  const handleEditTask = useCallback((task) => {
    navigate(`/tasks/${task.task_id}/edit`);
  }, [navigate]);

  const handleDeleteTask = useCallback((task) => {
    showConfirmDialog({
      title: t('common:dashboard.delete_task_title'),
      message: t('common:dashboard.delete_task_message', { title: task.title }),
      confirmText: t('common:dashboard.delete_task_confirm'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteTask(task.task_id);
          if (result.success) {
            showSuccess(t('common:dashboard.task_deleted'));
            await refreshTasks(); // Refresh cached data
          } else {
            showError(result.error || t('common:dashboard.delete_failed'));
          }
        } catch (error) {
          showError(t('common:dashboard.delete_failed'));
        }
      },
    });
  }, [showConfirmDialog, deleteTask, showSuccess, showError, refreshTasks, t]);

  // Permission checks
  const canUpdate = permissions.hasScope('task:update');
  const canDelete = permissions.hasScope('task:delete');
  const canChangeState = permissions.hasScope('task:state:update');

  // Show loading only for initial load, not background updates
  const isInitialLoading = loading && (!tasks || tasks.length === 0);

  return (
    <Box sx={{ mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight="bold">
            {t('common:dashboard.tasks_queue')}
          </Typography>
          {isBackgroundUpdate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RefreshIcon sx={{ 
                fontSize: 16, 
                color: 'text.secondary',
                animation: 'spin 1s linear infinite' 
              }} />
              <Typography variant="caption" color="text.secondary">
                {t('common:dashboard.updating')}
              </Typography>
            </Box>
          )}
          {isInitialLoading && <LinearProgress sx={{ width: 100, height: 2 }} />}
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={showMyTasks}
              onChange={handleViewModeToggle}
              color="primary"
            />
          }
          label={showMyTasks ? t('common:dashboard.my_tasks') : t('common:dashboard.all_department_tasks')}
          labelPlacement="start"
        />
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tasks DataGrid - Use the exact same component from TasksListPage */}
      <TasksDataGrid
        tasks={tasks}
        loading={isInitialLoading}
        canUpdate={canUpdate}
        canDelete={canDelete}
        canChangeState={canChangeState}
        onView={handleViewTask}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        onStateChange={handleStateChange}
        sortModel={DUE_DATE_SORT_MODEL}
        height={400}
      />

      {/* Global Task Actions Dialog */}
      <GlobalTaskActionsDialog
        dialogStates={dialogStates}
        dialogHandlers={dialogHandlers}
        loading={loading}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog />

      {/* Add keyframe animation for refresh icon */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
});

export default TaskQueueDataGridWrapper;